import { Descriptions, List, Modal, Select, Tag, Form, Input } from 'antd';
import Button from 'mixins/ui/button/button';
import React, { FC, useEffect } from 'react';
import {
    ApiTokenRequestDto,
    ApiTokenRequestStatus,
    ChangeApiTokenRequestStatusData,
} from '../types';
import {
    API_TOKEN_REQUEST_STATUS_COLORS,
    API_TOKEN_REQUEST_STATUS_LABELS,
    EDITABLE_API_TOKEN_REQUEST_STATUSES,
} from '../utils/constants';
import { formatTokenRequestDate } from '../utils/token-request-helpers';

interface TokenRequestDetailsModalProps {
    visible: boolean;
    loading: boolean;
    actionLoading: boolean;
    request?: ApiTokenRequestDto | null;
    canManage: boolean;
    onCancel: () => void;
    onChangeStatus: (data: ChangeApiTokenRequestStatusData) => void;
    onIssue: (request: ApiTokenRequestDto) => void;
    onDownloadAttachment: (
        request: ApiTokenRequestDto,
        attachmentId: number,
        fileName: string
    ) => void;
}

export const TokenRequestDetailsModal: FC<TokenRequestDetailsModalProps> = ({
    visible,
    loading,
    actionLoading,
    request,
    canManage,
    onCancel,
    onChangeStatus,
    onIssue,
    onDownloadAttachment,
}) => {
    const [form] = Form.useForm();

    useEffect(() => {
        if (!visible || !request || request.status === 'TOKEN_ISSUED') {
            return;
        }

        form.setFieldsValue({
            status: request.status,
            adminComment: request.adminComment || '',
        });
    }, [form, request, visible]);

    const submitStatus = () => {
        if (!request) {
            return;
        }

        form.validateFields().then((values) => {
            onChangeStatus({
                id: request.id,
                status: values.status,
                adminComment: values.adminComment,
            });
        });
    };

    return (
        <Modal
            title={
                request
                    ? `Заявка ${request.requestNumber}`
                    : 'Карточка заявки'
            }
            visible={visible}
            onCancel={onCancel}
            width={900}
            footer={[
                request?.status === 'APPROVED' && canManage ? (
                    <Button
                        key="issue"
                        color="gold"
                        loading={actionLoading}
                        onClick={() => onIssue(request)}
                    >
                        Выдать токен
                    </Button>
                ) : null,
                <Button key="close" onClick={onCancel}>
                    Закрыть
                </Button>,
            ].filter(Boolean)}
        >
            {loading || !request ? null : (
                <>
                    <Descriptions bordered column={2} size="small">
                        <Descriptions.Item label="№ обращения">
                            {request.requestNumber}
                        </Descriptions.Item>
                        <Descriptions.Item label="Дата подачи">
                            {formatTokenRequestDate(request.createdDate)}
                        </Descriptions.Item>
                        <Descriptions.Item label="ИИН/БИН">
                            {request.iinBin}
                        </Descriptions.Item>
                        <Descriptions.Item label="ФИО / Наименование">
                            {request.fullName}
                        </Descriptions.Item>
                        <Descriptions.Item label="Номер телефона">
                            {request.phone || '—'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Электронная почта">
                            {request.email || '—'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Статус">
                            <Tag color={API_TOKEN_REQUEST_STATUS_COLORS[request.status]}>
                                {API_TOKEN_REQUEST_STATUS_LABELS[request.status]}
                            </Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="Дата изменения">
                            {formatTokenRequestDate(request.updatedDate)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Дата получения токена">
                            {formatTokenRequestDate(request.tokenReceivedDate)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Токен выдан">
                            {request.tokenIssued ? 'Да' : 'Нет'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Сообщение" span={2}>
                            <div className="admin-token-generations__details-text">
                                {request.message || '—'}
                            </div>
                        </Descriptions.Item>
                        <Descriptions.Item
                            label="Комментарий администратора"
                            span={2}
                        >
                            <div className="admin-token-generations__details-text">
                                {request.adminComment || '—'}
                            </div>
                        </Descriptions.Item>
                    </Descriptions>

                    <div className="admin-token-generations__details-section">
                        <h4>Вложения</h4>
                        {request.attachments?.length ? (
                            <List
                                bordered
                                dataSource={request.attachments}
                                renderItem={(attachment) => (
                                    <List.Item
                                        actions={[
                                            <Button
                                                key="download"
                                                onClick={() =>
                                                    onDownloadAttachment(
                                                        request,
                                                        attachment.id,
                                                        attachment.fileName
                                                    )
                                                }
                                            >
                                                Скачать
                                            </Button>,
                                        ]}
                                    >
                                        <List.Item.Meta
                                            title={attachment.fileName}
                                            description={`Загружен: ${formatTokenRequestDate(
                                                attachment.uploadDate
                                            )}`}
                                        />
                                    </List.Item>
                                )}
                            />
                        ) : (
                            <div>Вложения отсутствуют</div>
                        )}
                    </div>

                    {canManage && request.status !== 'TOKEN_ISSUED' ? (
                        <div className="admin-token-generations__details-section">
                            <h4>Изменение статуса</h4>
                            <Form form={form} layout="vertical">
                                <Form.Item
                                    label="Статус"
                                    name="status"
                                    rules={[
                                        {
                                            required: true,
                                            message: 'Выберите статус заявки',
                                        },
                                    ]}
                                >
                                    <Select size="large">
                                        {EDITABLE_API_TOKEN_REQUEST_STATUSES.map(
                                            (status: Exclude<
                                                ApiTokenRequestStatus,
                                                'TOKEN_ISSUED'
                                            >) => (
                                                <Select.Option
                                                    key={status}
                                                    value={status}
                                                >
                                                    {
                                                        API_TOKEN_REQUEST_STATUS_LABELS[
                                                            status
                                                        ]
                                                    }
                                                </Select.Option>
                                            )
                                        )}
                                    </Select>
                                </Form.Item>
                                <Form.Item
                                    label="Комментарий администратора"
                                    name="adminComment"
                                >
                                    <Input.TextArea
                                        rows={3}
                                        maxLength={2000}
                                    />
                                </Form.Item>
                                <Button
                                    color="gold"
                                    loading={actionLoading}
                                    onClick={submitStatus}
                                >
                                    Сохранить статус
                                </Button>
                            </Form>
                        </div>
                    ) : null}
                </>
            )}
        </Modal>
    );
};